import { __rest } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import React__default, { useState, useCallback, useEffect } from 'react';
import { styled } from '@mui/material';
import MuiBox from '@mui/material/Box';
import MuiCollapse from '@mui/material/Collapse';
import MuiDivider from '@mui/material/Divider';
import MuiTypography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Notification from '../Notification/Notification.js';
import { ReactComponent as ForwardRef$2 } from '../../assets/icons/OutlineArrowDownSmall.svg.js';
import { ReactComponent as ForwardRef$1 } from '../../assets/icons/OutlineArrowUpSmall.svg.js';
import { ReactComponent as ForwardRef } from '../../assets/icons/CircleLarge.svg.js';

const IconWrapper = styled(MuiBox) `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  ${({ theme, rtl }) => `
   margin: ${rtl ? theme.spacing(0, 0, 0, 2.5) : theme.spacing(0, 2.5, 0, 0)} ;
`};
`;
const Root = styled(MuiBox) `
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-direction: column;
`;
const Header = styled(MuiBox, {
    shouldForwardProp: prop => !['collapsible', 'noHorizontalPadding'].includes(prop),
}) `
  display: flex;
  align-items: center;
  width: 100%;
  transition: color 0.1s linear, border 0.1s linear;
  ${({ theme, collapsible, noHorizontalPadding }) => `
  padding: ${noHorizontalPadding ? theme.spacing(1.75, 0, 1.75, 0) : theme.spacing(1.75, 3, 1.75, 4)};
  cursor: ${collapsible ? 'pointer' : 'default'};
  color: ${theme.palette.N30.main};
  .MuiDivider-root {
    border-color: ${theme.palette.N40.main};
  }
    &:hover {
      color: ${theme.palette.N20.main};
      .MuiDivider-root{
        border-color: ${theme.palette.N30.main};
      }
      svg {
        fill: ${theme.palette.primary.main};
      }
    }
  `}
`;
const Divider = styled(MuiDivider, { shouldForwardProp: prop => prop !== 'collapsible' }) `
  ${({ theme, collapsible }) => `
    display: flex;
    border-color: ${theme.palette.N30.main};
    margin: ${collapsible ? theme.spacing(0, 2) : theme.spacing(0, 0, 0, 2)};
    flex: 1;
  `}
`;
const Collapse = styled(MuiCollapse, { shouldForwardProp: prop => prop !== 'noHorizontalPadding' }) `
  width: 100%;
  ${({ theme, noHorizontalPadding }) => `
    color: ${theme.palette.N20.main};
    .MuiCollapse-wrapperInner {
      margin: ${noHorizontalPadding ? theme.spacing(0, 0) : theme.spacing(0, 4)};
    }
  `}
`;
const StatusIcon = styled(ForwardRef) `
  color: ${({ theme, status = 'warning' }) => theme.palette[status === 'inactive' ? 'N80' : status].main};
  circle {
    stroke: ${({ theme, status = 'warning' }) => theme.palette[status === 'inactive' ? 'N30' : 'N95'].main};
  }
`;
const InspectorWidget = (_a) => {
    var { title, icon, collapsible = true, status = 'warning', onToggle, className, rtl, defaultIsOpen = false, 'data-testid': testId = 'inspector-widget', noHorizontalPadding = false, hideIcon = false, hideDivider = false, children, error, loading, isOpen } = _a, rest = __rest(_a, ["title", "icon", "collapsible", "status", "onToggle", "className", "rtl", "defaultIsOpen", 'data-testid', "noHorizontalPadding", "hideIcon", "hideDivider", "children", "error", "loading", "isOpen"]);
    const [open, setOpen] = useState(defaultIsOpen);
    const onToggleClick = useCallback(() => {
        if (collapsible && children) {
            setOpen(value => {
                onToggle === null || onToggle === void 0 ? void 0 : onToggle(!value);
                return !value;
            });
        }
    }, [onToggle, children, collapsible]);
    useEffect(() => {
        if (!collapsible) {
            setOpen(true);
        }
    }, [collapsible]);
    useEffect(() => {
        if (isOpen !== undefined) {
            setOpen(isOpen);
        }
    }, [isOpen]);
    const labelText = typeof title === 'string' ? title : undefined;
    return (jsxs(Root, Object.assign({ className: className, "data-testid": testId, "aria-label": labelText }, { children: [jsxs(Header, Object.assign({ collapsible: collapsible, "data-testid": `${testId}-header`, noHorizontalPadding: noHorizontalPadding, onClick: onToggleClick }, { children: [!hideIcon && (jsx(IconWrapper, Object.assign({ className: 'icon-wrapper', "data-testid": "icon-wrapper", rtl: rtl }, { children: icon ? icon : jsx(StatusIcon, { "data-testid": "status", status: status }) }))), React__default.isValidElement(title) ? title : jsx(MuiTypography, Object.assign({ variant: "subtitle2" }, { children: title })), !hideDivider && jsx(Divider, { variant: "middle", collapsible: collapsible }), collapsible && (jsx(MuiBox, Object.assign({ sx: { marginLeft: 'auto', order: 2 } }, { children: children &&
                            (open ? (jsx(ForwardRef$1, { "data-testid": "collapsible-less" })) : (jsx(ForwardRef$2, { "data-testid": "collapsible-more" }))) })))] })), jsxs(Collapse, Object.assign({ unmountOnExit: true, in: open }, rest, { noHorizontalPadding: noHorizontalPadding }, { children: [!error && !loading && children, error && (jsx(Notification, Object.assign({ "data-testid": `${testId}-error`, type: "error" }, { children: error }))), loading && !error && jsx(LinearProgress, { "data-testid": `${testId}-loading` })] }))] })));
};

export { InspectorWidget as default };
//# sourceMappingURL=InspectorWidget.js.map
